/**
 * Draft Management Hooks
 *
 * Provides React hooks for:
 * - Auto-saving drafts at intervals
 * - Collaborative editing lock management
 * - Save status tracking
 *
 * These hooks integrate with the lock API endpoints to enable
 * collaborative editing with conflict prevention.
 */

import { useState, useCallback, useEffect, useRef } from "react";

import type { ConfigType } from "../components/diff/types";

import type { ConfigApiClient } from "./use-config-api";

// ============================================================================
// Types
// ============================================================================

/** Lock info from API */
export interface LockInfo {
  readonly lockId: string;
  readonly verticalKey: string;
  readonly environment: string;
  readonly filePath: string;
  readonly lockedBy: string;
  readonly lockedByName?: string | undefined;
  readonly lockedByEmail?: string | undefined;
  readonly lockedAt: string;
  readonly expiresAt: string;
  readonly isOwner: boolean;
}

/** Save status for UI display */
export type SaveStatus = "idle" | "saving" | "saved" | "error";

/** Lock status for UI display */
export type LockStatus = "unlocked" | "acquiring" | "locked" | "locked_by_other" | "error";

/** Auto-save hook options */
export interface UseAutoSaveOptions {
  /** Interval in milliseconds (default: 30000 = 30s) */
  readonly intervalMs?: number | undefined;
  /** Whether auto-save is enabled (default: true) */
  readonly enabled?: boolean | undefined;
  /** Callback when save succeeds */
  readonly onSaveSuccess?: (() => void) | undefined;
  /** Callback when save fails */
  readonly onSaveError?: ((error: Error) => void) | undefined;
}

/** Lock hook options */
export interface UseDraftLockingOptions {
  /** Lock duration in milliseconds (default: 60000 = 60s) */
  readonly lockDurationMs?: number | undefined;
  /** Heartbeat interval in milliseconds (default: 30000 = 30s) */
  readonly heartbeatIntervalMs?: number | undefined;
  /** Whether to auto-acquire lock on mount */
  readonly autoAcquire?: boolean | undefined;
  /** Callback when lock is lost to another user */
  readonly onLockLost?: ((heldBy: string) => void) | undefined;
  /** Callback when lock errors occur */
  readonly onLockError?: ((error: Error) => void) | undefined;
}

// ============================================================================
// Lock API Client Extension
// ============================================================================

/** Lock API client configuration */
export interface LockApiClientConfig {
  readonly baseUrl: string;
  readonly getAuthToken: () => string | null;
}

/** API response wrapper */
interface ApiResponse<T> {
  readonly data: T;
  readonly message?: string;
  readonly timestamp: string;
  readonly requestId?: string;
}

/** Lock error response */
interface LockErrorResponse {
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly heldBy?: string;
    readonly expiresAt?: string;
  };
}

/**
 * Create a lock API client.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createLockApiClient(config: LockApiClientConfig) {
  const { baseUrl, getAuthToken } = config;

  const fetchWithAuth = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
    const token = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      const errorResponse = data as LockErrorResponse;
      const error = new Error(
        errorResponse.error?.message || `API error: ${response.status}`
      ) as Error & {
        code?: string | undefined;
        heldBy?: string | undefined;
        expiresAt?: string | undefined;
      };
      if (errorResponse.error?.code !== undefined) {
        error.code = errorResponse.error.code;
      }
      if (errorResponse.error?.heldBy !== undefined) {
        error.heldBy = errorResponse.error.heldBy;
      }
      if (errorResponse.error?.expiresAt !== undefined) {
        error.expiresAt = errorResponse.error.expiresAt;
      }
      throw error;
    }

    return data as T;
  };

  return {
    /**
     * Acquire a lock on a config file.
     */
    acquireLock: async (
      verticalKey: string,
      filePath: string,
      durationMs?: number
    ): Promise<LockInfo> => {
      const response = await fetchWithAuth<ApiResponse<LockInfo>>("/api/v1/locks/acquire", {
        method: "POST",
        body: JSON.stringify({ verticalKey, filePath, durationMs }),
      });
      return response.data;
    },

    /**
     * Release a lock.
     */
    releaseLock: async (verticalKey: string, filePath: string): Promise<void> => {
      await fetchWithAuth<ApiResponse<unknown>>("/api/v1/locks/release", {
        method: "POST",
        body: JSON.stringify({ verticalKey, filePath }),
      });
    },

    /**
     * Send heartbeat to extend lock.
     */
    heartbeat: async (
      verticalKey: string,
      filePath: string,
      durationMs?: number
    ): Promise<{ lockId: string; expiresAt: string }> => {
      const response = await fetchWithAuth<ApiResponse<{ lockId: string; expiresAt: string }>>(
        "/api/v1/locks/heartbeat",
        {
          method: "POST",
          body: JSON.stringify({ verticalKey, filePath, durationMs }),
        }
      );
      return response.data;
    },

    /**
     * Check lock status.
     */
    checkLock: async (verticalKey: string, filePath: string): Promise<LockInfo | null> => {
      const response = await fetchWithAuth<ApiResponse<LockInfo | null>>(
        `/api/v1/locks/check?verticalKey=${encodeURIComponent(verticalKey)}&filePath=${encodeURIComponent(filePath)}`
      );
      return response.data;
    },

    /**
     * List locks for a vertical.
     */
    listLocks: async (verticalKey: string): Promise<{ items: LockInfo[]; total: number }> => {
      const response = await fetchWithAuth<ApiResponse<{ items: LockInfo[]; total: number }>>(
        `/api/v1/locks?verticalKey=${encodeURIComponent(verticalKey)}`
      );
      return response.data;
    },

    /**
     * List user's locks.
     */
    listMyLocks: async (): Promise<{ items: LockInfo[]; total: number }> => {
      const response =
        await fetchWithAuth<ApiResponse<{ items: LockInfo[]; total: number }>>("/api/v1/locks/my");
      return response.data;
    },
  };
}

export type LockApiClient = ReturnType<typeof createLockApiClient>;

// ============================================================================
// Auto-Save Hook
// ============================================================================

/**
 * Hook for auto-saving draft content at regular intervals.
 *
 * Features:
 * - Debounced saves to avoid excessive API calls
 * - Dirty tracking to only save when content has changed
 * - Status tracking for UI feedback
 * - Error handling with callbacks
 *
 * @example
 * const { saveStatus, markDirty, saveNow, lastSavedAt } = useAutoSave({
 *   client,
 *   verticalKey: "health-insurance",
 *   configType: "eligibility",
 *   getContent: () => editorContent,
 * });
 */
export function useAutoSave(
  client: ConfigApiClient,
  verticalKey: string,
  configType: ConfigType,
  getContent: () => unknown,
  options: UseAutoSaveOptions = {}
): {
  saveStatus: SaveStatus;
  isDirty: boolean;
  markDirty: () => void;
  saveNow: () => Promise<void>;
  lastSavedAt: string | null;
  error: string | null;
} {
  const { intervalMs = 30000, enabled = true, onSaveSuccess, onSaveError } = options;

  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Refs for tracking between renders
  const isDirtyRef = useRef(isDirty);
  const isSavingRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Mark content as dirty (content has changed)
  const markDirty = useCallback(() => {
    setIsDirty(true);
    setSaveStatus("idle");
  }, []);

  // Save content to server
  const saveNow = useCallback(async (): Promise<void> => {
    if (isSavingRef.current) return;
    if (!isDirtyRef.current) return;

    isSavingRef.current = true;
    setSaveStatus("saving");
    setError(null);

    try {
      const content = getContent();
      await client.saveDraft(verticalKey, configType, content);

      setIsDirty(false);
      setSaveStatus("saved");
      setLastSavedAt(new Date().toISOString());

      onSaveSuccess?.();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Save failed";
      setSaveStatus("error");
      setError(errorMsg);

      onSaveError?.(err instanceof Error ? err : new Error(errorMsg));
    } finally {
      isSavingRef.current = false;
    }
  }, [client, verticalKey, configType, getContent, onSaveSuccess, onSaveError]);

  // Set up auto-save interval
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      if (isDirtyRef.current && !isSavingRef.current) {
        void saveNow();
      }
    }, intervalMs);

    return (): void => {
      clearInterval(interval);
    };
  }, [enabled, intervalMs, saveNow]);

  // Save on unmount if dirty
  useEffect(() => {
    return (): void => {
      if (isDirtyRef.current && !isSavingRef.current) {
        // Fire and forget - component is unmounting
        void saveNow();
      }
    };
  }, [saveNow]);

  return {
    saveStatus,
    isDirty,
    markDirty,
    saveNow,
    lastSavedAt,
    error,
  };
}

// ============================================================================
// Draft Locking Hook
// ============================================================================

/**
 * Hook for managing collaborative editing locks.
 *
 * Features:
 * - Automatic lock acquisition on mount (optional)
 * - Heartbeat to keep lock alive
 * - Lock status tracking for UI
 * - Conflict detection when another user holds the lock
 * - Automatic lock release on unmount
 *
 * @example
 * const { lockStatus, lockInfo, acquireLock, releaseLock } = useDraftLocking({
 *   client: lockClient,
 *   verticalKey: "health-insurance",
 *   filePath: "eligibility.json",
 *   autoAcquire: true,
 *   onLockLost: (heldBy) => alert(`Lock taken by ${heldBy}`),
 * });
 */
export function useDraftLocking(
  client: LockApiClient,
  verticalKey: string,
  filePath: string,
  options: UseDraftLockingOptions = {}
): {
  lockStatus: LockStatus;
  lockInfo: LockInfo | null;
  heldByInfo: { name?: string | undefined; email?: string | undefined; userId: string } | null;
  acquireLock: () => Promise<boolean>;
  releaseLock: () => Promise<void>;
  checkLock: () => Promise<void>;
  error: string | null;
} {
  const {
    lockDurationMs = 60000,
    heartbeatIntervalMs = 30000,
    autoAcquire = false,
    onLockLost,
    onLockError,
  } = options;

  const [lockStatus, setLockStatus] = useState<LockStatus>("unlocked");
  const [lockInfo, setLockInfo] = useState<LockInfo | null>(null);
  const [heldByInfo, setHeldByInfo] = useState<{
    name?: string | undefined;
    email?: string | undefined;
    userId: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isLockedRef = useRef(false);

  // Stop heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Start heartbeat
  const startHeartbeat = useCallback(() => {
    stopHeartbeat();

    heartbeatIntervalRef.current = setInterval(async () => {
      if (!isLockedRef.current) return;

      try {
        const result = await client.heartbeat(verticalKey, filePath, lockDurationMs);
        setLockInfo((prev) => (prev ? { ...prev, expiresAt: result.expiresAt } : null));
      } catch (err) {
        const errorWithCode = err as Error & { code?: string; heldBy?: string };

        if (errorWithCode.code === "LOCK_HELD_BY_OTHER") {
          // Lock was taken by someone else
          isLockedRef.current = false;
          setLockStatus("locked_by_other");
          setHeldByInfo(errorWithCode.heldBy ? { userId: errorWithCode.heldBy } : null);
          stopHeartbeat();
          onLockLost?.(errorWithCode.heldBy ?? "another user");
        } else {
          // Heartbeat failed — lock may have expired. Disable auto-save.
          isLockedRef.current = false;
          setLockStatus("error");
          stopHeartbeat();
          setError(err instanceof Error ? err.message : "Heartbeat failed");
          onLockError?.(err instanceof Error ? err : new Error("Heartbeat failed"));
        }
      }
    }, heartbeatIntervalMs);
  }, [
    client,
    verticalKey,
    filePath,
    lockDurationMs,
    heartbeatIntervalMs,
    stopHeartbeat,
    onLockLost,
    onLockError,
  ]);

  // Acquire lock
  const acquireLock = useCallback(async (): Promise<boolean> => {
    setLockStatus("acquiring");
    setError(null);

    try {
      const info = await client.acquireLock(verticalKey, filePath, lockDurationMs);
      setLockInfo(info);
      setLockStatus("locked");
      isLockedRef.current = true;
      setHeldByInfo(null);

      // Start heartbeat to keep lock alive
      startHeartbeat();

      return true;
    } catch (err) {
      const errorWithCode = err as Error & {
        code?: string;
        heldBy?: string;
        expiresAt?: string;
      };

      if (errorWithCode.code === "LOCK_HELD_BY_OTHER") {
        setLockStatus("locked_by_other");
        setHeldByInfo({
          userId: errorWithCode.heldBy ?? "unknown",
        });
        setError(`File is locked by ${errorWithCode.heldBy ?? "another user"}`);
      } else {
        setLockStatus("error");
        setError(err instanceof Error ? err.message : "Failed to acquire lock");
        onLockError?.(err instanceof Error ? err : new Error("Failed to acquire lock"));
      }

      return false;
    }
  }, [client, verticalKey, filePath, lockDurationMs, startHeartbeat, onLockError]);

  // Release lock
  const releaseLock = useCallback(async (): Promise<void> => {
    stopHeartbeat();

    if (!isLockedRef.current) return;

    try {
      await client.releaseLock(verticalKey, filePath);
    } catch (err) {
      // Log but don't throw - we're cleaning up
      console.error("Failed to release lock:", err);
    } finally {
      isLockedRef.current = false;
      setLockInfo(null);
      setLockStatus("unlocked");
    }
  }, [client, verticalKey, filePath, stopHeartbeat]);

  // Check lock status
  const checkLock = useCallback(async (): Promise<void> => {
    try {
      const info = await client.checkLock(verticalKey, filePath);

      if (info) {
        setLockInfo(info);
        if (info.isOwner) {
          setLockStatus("locked");
          isLockedRef.current = true;
        } else {
          setLockStatus("locked_by_other");
          setHeldByInfo({
            userId: info.lockedBy,
            name: info.lockedByName,
            email: info.lockedByEmail,
          });
        }
      } else {
        setLockStatus("unlocked");
        setLockInfo(null);
        setHeldByInfo(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to check lock");
    }
  }, [client, verticalKey, filePath]);

  // Auto-acquire on mount
  useEffect(() => {
    if (autoAcquire) {
      void acquireLock();
    } else {
      // Just check the lock status
      void checkLock();
    }

    // Release lock on unmount
    return (): void => {
      stopHeartbeat();
      if (isLockedRef.current) {
        // Fire and forget - component is unmounting
        void client.releaseLock(verticalKey, filePath).catch((err) => {
          console.error("Failed to release lock on unmount:", err);
        });
        isLockedRef.current = false;
      }
    };
  }, []);

  return {
    lockStatus,
    lockInfo,
    heldByInfo,
    acquireLock,
    releaseLock,
    checkLock,
    error,
  };
}

// ============================================================================
// Combined Draft Management Hook
// ============================================================================

/**
 * Combined hook for auto-save with locking.
 *
 * Combines useAutoSave and useDraftLocking for a complete
 * collaborative editing experience.
 *
 * @example
 * const {
 *   saveStatus,
 *   lockStatus,
 *   canEdit,
 *   markDirty,
 *   saveNow,
 * } = useDraftManagement({
 *   configClient,
 *   lockClient,
 *   verticalKey: "health-insurance",
 *   configType: "eligibility",
 *   filePath: "eligibility.json",
 *   getContent: () => editorContent,
 * });
 */
export function useDraftManagement(
  configClient: ConfigApiClient,
  lockClient: LockApiClient,
  verticalKey: string,
  configType: ConfigType,
  filePath: string,
  getContent: () => unknown,
  options: UseAutoSaveOptions & UseDraftLockingOptions = {}
): {
  // Save state
  saveStatus: SaveStatus;
  isDirty: boolean;
  markDirty: () => void;
  saveNow: () => Promise<void>;
  lastSavedAt: string | null;
  saveError: string | null;
  // Lock state
  lockStatus: LockStatus;
  lockInfo: LockInfo | null;
  heldByInfo: { name?: string | undefined; email?: string | undefined; userId: string } | null;
  acquireLock: () => Promise<boolean>;
  releaseLock: () => Promise<void>;
  lockError: string | null;
  // Computed
  canEdit: boolean;
  isReadOnly: boolean;
} {
  const {
    intervalMs,
    enabled,
    onSaveSuccess,
    onSaveError,
    lockDurationMs,
    heartbeatIntervalMs,
    autoAcquire = true,
    onLockLost,
    onLockError,
  } = options;

  // Use locking hook
  const {
    lockStatus,
    lockInfo,
    heldByInfo,
    acquireLock,
    releaseLock,
    error: lockError,
  } = useDraftLocking(lockClient, verticalKey, filePath, {
    lockDurationMs,
    heartbeatIntervalMs,
    autoAcquire,
    onLockLost,
    onLockError,
  });

  // Only enable auto-save when we have the lock
  const canEdit = lockStatus === "locked";
  const autoSaveEnabled = enabled !== false && canEdit;

  // Use auto-save hook
  const {
    saveStatus,
    isDirty,
    markDirty,
    saveNow,
    lastSavedAt,
    error: saveError,
  } = useAutoSave(configClient, verticalKey, configType, getContent, {
    intervalMs,
    enabled: autoSaveEnabled,
    onSaveSuccess,
    onSaveError,
  });

  return {
    // Save state
    saveStatus,
    isDirty,
    markDirty,
    saveNow,
    lastSavedAt,
    saveError,
    // Lock state
    lockStatus,
    lockInfo,
    heldByInfo,
    acquireLock,
    releaseLock,
    lockError,
    // Computed
    canEdit,
    isReadOnly: !canEdit,
  };
}
